import { useQueryClient } from "@tanstack/react-query";
import type { InfiniteData } from "@tanstack/react-query";
import { useCallback } from "react";

import { getApiRuntime } from "@/shared/api/client";
import { INTERNAL_ERROR_CODE } from "@/shared/constants/http";
import type { UnifiedError } from "@/shared/types/api";

import { recordItemView } from "../api";
import { mineQueryKeys } from "../query-options";
import type { Item, ItemsResponse, RecordItemViewResponse } from "../types";

function withUpdatedViews(item: Item, result: RecordItemViewResponse): Item {
  if (item.id !== result.id || (item.views ?? 0) >= result.views) {
    return item;
  }

  return { ...item, views: result.views };
}

function updateItemsPage(page: ItemsResponse, result: RecordItemViewResponse): ItemsResponse {
  const items = page.items.map((item) => withUpdatedViews(item, result));
  return items.some((item, index) => item !== page.items[index])
    ? { ...page, items }
    : page;
}

function isRetryable(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return false;
  }

  const { code, status } = error as UnifiedError;
  return code !== INTERNAL_ERROR_CODE.CANCELLED
    && (status === undefined || status === 408 || status >= 500);
}

export function useRecordItemView() {
  const queryClient = useQueryClient();

  return useCallback((itemId: string, operationId: string = crypto.randomUUID()) => {
    const token = getApiRuntime().getToken?.();
    if (!token) {
      return;
    }

    const send = async () => {
      let result: RecordItemViewResponse;

      try {
        result = await recordItemView(itemId, operationId);
      } catch (error) {
        if (!isRetryable(error) || getApiRuntime().getToken?.() !== token) {
          return;
        }

        try {
          result = await recordItemView(itemId, operationId);
        } catch {
          return;
        }
      }

      if (getApiRuntime().getToken?.() !== token) {
        return;
      }

      queryClient.setQueryData<Item>(mineQueryKeys.itemDetail(itemId), (item) =>
        item ? withUpdatedViews(item, result) : item,
      );
      queryClient.setQueriesData<ItemsResponse | InfiniteData<ItemsResponse>>(
        {
          predicate: ({ queryKey }) =>
            queryKey[0] === "mine" && queryKey[1] === "items"
            && typeof queryKey[2] === "object",
        },
        (data) => {
          if (!data) {
            return data;
          }

          if ("pages" in data) {
            const pages = data.pages.map((page) => updateItemsPage(page, result));
            if (pages.every((page, index) => page === data.pages[index])) {
              return data;
            }

            return {
              ...data,
              pages,
            };
          }

          return updateItemsPage(data, result);
        },
      );
      queryClient.setQueriesData<ItemsResponse>(
        { queryKey: ["mine", "search-preview"] },
        (page) => page ? updateItemsPage(page, result) : page,
      );
    };

    void send();
  }, [queryClient]);
}
